import { PencilIcon, TrashIcon, PlusIcon, MinusIcon } from '@heroicons/react/24/outline';
import {
  PencilIcon as PencilIconSolid,
  TrashIcon as TrashIconSolid,
  PlusIcon as PlusIconSolid,
  MinusIcon as MinusIconSolid,
} from '@heroicons/react/24/solid';
import { FlagIcon as FlagIconOutline } from '@heroicons/react/24/outline';
import { FlagIcon as FlagIconSolid } from '@heroicons/react/24/solid';
import { ViewfinderCircleIcon as ViewfinderCircleIconOutline } from '@heroicons/react/24/outline';
import { ViewfinderCircleIcon as ViewfinderCircleIconSolid } from '@heroicons/react/24/solid';
import { useState, useEffect, useRef } from 'react';
import { Alert, AlertTitle, AlertDescription, AlertActions } from '../../components_catalyst/alert';
import { Button } from '../../components_catalyst/button';
import { InformationCircleIcon as InformationCircleIconOutline } from '@heroicons/react/24/outline';
import { InformationCircleIcon as InformationCircleIconSolid } from '@heroicons/react/24/solid';
import { useNavigate } from 'react-router-dom';
import { StarIcon as StarIconOutline } from '@heroicons/react/24/outline';
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid';
import { PushPinIcon as PushPinIconOutline } from '../../components_misc/PushPinIcon';
import { PushPinIconSolid } from '../../components_misc/PushPinIcon';

/**
 * A component that displays a bar of action buttons for adding, editing, deleting, and flagging items.
 *
 * Icon Behaviors:
 * - Add/Edit: Shows outlined icon by default, changes to solid on hover
 * - Delete: Shows outlined icon by default, changes to solid on hover. Triggers confirmation dialog.
 * - Flag: Shows solid icon when item is flagged, otherwise behaves like Add/Edit (outlined by default, solid on hover)
 */

interface AddEditDeleteBar1Props {
  onInfo?: (() => void) | string;
  onAdd?: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
  onRemove?: () => void;
  onFlag?: () => void;
  onStar?: () => void;
  onPin?: () => void;
  onHighlight?: () => void;
  infoAriaLabel?: string;
  showInfo?: boolean;
  showAdd?: boolean;
  showEdit?: boolean;
  showDelete?: boolean;
  showRemove?: boolean;
  showFlag?: boolean;
  showStar?: boolean;
  showPin?: boolean;
  showHighlight?: boolean;
  isVisible?: boolean;
  isFlagged?: boolean;
  isStarred?: boolean;
  isPinned?: boolean;
  isHighlighted?: boolean;
  transparent?: boolean;
  className?: string;
  actionBarActive?: boolean;
  onActionBarEscape?: () => void;
}

export function AddEditDeleteBar1({
  onInfo,
  onAdd,
  onEdit,
  onDelete,
  onRemove,
  onFlag,
  infoAriaLabel = 'View information',
  showInfo = false,
  showAdd = true,
  showEdit = true,
  showDelete = true,
  showRemove = false,
  showFlag = false,
  showStar = false,
  showPin = false,
  showHighlight = false,
  isVisible = false,
  isFlagged = false,
  isStarred = false,
  isPinned = false,
  isHighlighted = false,
  onStar,
  onPin,
  onHighlight,
  transparent = false,
  className,
  actionBarActive = false,
  onActionBarEscape,
}: AddEditDeleteBar1Props) {
  const [isDeleteAlertOpen, setIsDeleteAlertOpen] = useState(false);
  const [isRemoveAlertOpen, setIsRemoveAlertOpen] = useState(false);
  const [focusedButtonIndex, setFocusedButtonIndex] = useState(-1);
  const classNames = ['p-1', 'text-gray-700', 'rounded', 'hover:text-blue-600', 'group'];
  const navigate = useNavigate();
  const toolbarRef = useRef<HTMLDivElement>(null);

  // Make buttons focusable when action bar is visible for arrow key navigation
  const buttonTabIndex = -1; // Always -1, only reachable via arrow keys

  // Focus first button when action bar becomes active
  useEffect(() => {
    if (actionBarActive && toolbarRef.current) {
      const buttons = Array.from(toolbarRef.current.querySelectorAll('button'));
      const visibleButtons = buttons.filter(
        (button) => !button.parentElement?.classList.contains('opacity-0'),
      );
      if (visibleButtons.length > 0) {
        visibleButtons[0].focus();
        setFocusedButtonIndex(0);
      }
    }
  }, [actionBarActive]);

  const handleInfo = (e: React.MouseEvent | React.KeyboardEvent) => {
    e.stopPropagation();
    if (typeof onInfo === 'string') {
      navigate(onInfo);
    } else if (onInfo) {
      onInfo();
    }
  };

  const handleToolbarKeyDown = (e: React.KeyboardEvent) => {
    // Only handle keys when action bar is active
    if (!actionBarActive) return;

    e.stopPropagation();

    // Get all visible buttons
    const buttons = Array.from(e.currentTarget.querySelectorAll('button'));
    const visibleButtons = buttons.filter(
      (button) => !button.parentElement?.classList.contains('opacity-0'),
    );

    switch (e.key) {
      case 'Escape':
        e.preventDefault();
        onActionBarEscape?.();
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        e.preventDefault();
        setFocusedButtonIndex((prev) => {
          const newIndex = prev <= 0 ? visibleButtons.length - 1 : prev - 1;
          visibleButtons[newIndex]?.focus();
          return newIndex;
        });
        break;
      case 'ArrowRight':
      case 'ArrowDown':
        e.preventDefault();
        setFocusedButtonIndex((prev) => {
          const newIndex = prev >= visibleButtons.length - 1 ? 0 : prev + 1;
          visibleButtons[newIndex]?.focus();
          return newIndex;
        });
        break;
    }
  };

  return (
    <div className="relative">
      <div
        ref={toolbarRef}
        className={`flex gap-1 transition-opacity duration-200 rounded px-1 ${
          !transparent ? 'bg-white/80' : ''
        } ${isVisible ? 'opacity-100' : 'opacity-0'} ${className || ''}`}
        role="toolbar"
        tabIndex={-1}
        onKeyDown={handleToolbarKeyDown}
        aria-label="Item actions"
      >
        {showInfo && (
          <button
            onClick={handleInfo}
            tabIndex={buttonTabIndex}
            className={classNames.join(' ')}
            aria-label={infoAriaLabel}
          >
            <InformationCircleIconOutline className="h-8 w-8 group-hover:hidden" />
            <InformationCircleIconSolid className="h-8 w-8 hidden group-hover:block" />
          </button>
        )}
        {showAdd && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onAdd?.();
            }}
            tabIndex={buttonTabIndex}
            className={classNames.join(' ')}
            aria-label="Add item"
          >
            <PlusIcon className="h-6 w-6 group-hover:hidden" />
            <PlusIconSolid className="h-6 w-6 hidden group-hover:block" />
          </button>
        )}
        {showEdit && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onEdit?.();
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                e.stopPropagation();
                onEdit?.();
              }
            }}
            tabIndex={buttonTabIndex}
            className={classNames.join(' ')}
            aria-label="Edit item"
          >
            <PencilIcon className="h-6 w-6 group-hover:hidden" />
            <PencilIconSolid className="h-6 w-6 hidden group-hover:block" />
          </button>
        )}
        {showDelete && (
          <>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setIsDeleteAlertOpen(true);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  e.stopPropagation();
                  setIsDeleteAlertOpen(true);
                }
              }}
              tabIndex={buttonTabIndex}
              className={classNames.join(' ')}
              aria-label="Delete item"
            >
              <TrashIcon className="h-6 w-6 group-hover:hidden" />
              <TrashIconSolid className="h-6 w-6 hidden group-hover:block" />
            </button>

            <Alert
              open={isDeleteAlertOpen}
              onClose={() => setIsDeleteAlertOpen(false)}
              className="relative z-50"
            >
              <AlertTitle>Delete?</AlertTitle>
              <AlertDescription>This action cannot be undone.</AlertDescription>
              <AlertActions>
                <Button plain onClick={() => setIsDeleteAlertOpen(false)}>
                  Cancel
                </Button>
                <Button
                  onClick={() => {
                    onDelete?.();
                    setIsDeleteAlertOpen(false);
                  }}
                >
                  Delete
                </Button>
              </AlertActions>
            </Alert>
          </>
        )}
        {showFlag && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onFlag?.();
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                e.stopPropagation();
                onFlag?.();
              }
            }}
            tabIndex={buttonTabIndex}
            className={classNames.join(' ')}
            aria-label={isFlagged ? 'Remove flag' : 'Flag item'}
            aria-pressed={isFlagged}
          >
            {isFlagged ? (
              <FlagIconSolid className="h-6 w-6" />
            ) : (
              <>
                <FlagIconOutline className="h-6 w-6 group-hover:hidden" />
                <FlagIconSolid className="h-6 w-6 hidden group-hover:block" />
              </>
            )}
          </button>
        )}
        {showStar && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onStar?.();
            }}
            tabIndex={buttonTabIndex}
            className={classNames.join(' ')}
            aria-label={isStarred ? 'Remove star' : 'Star item'}
            aria-pressed={isStarred}
          >
            {isStarred ? (
              <StarIconSolid className="h-6 w-6" />
            ) : (
              <>
                <StarIconOutline className="h-6 w-6 group-hover:hidden" />
                <StarIconSolid className="h-6 w-6 hidden group-hover:block" />
              </>
            )}
          </button>
        )}
        {showPin && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onPin?.();
            }}
            tabIndex={buttonTabIndex}
            className={classNames.join(' ')}
            aria-label={isPinned ? 'Unpin item' : 'Pin item'}
            aria-pressed={isPinned}
          >
            {isPinned ? (
              <PushPinIconSolid className="h-6 w-6" />
            ) : (
              <>
                <PushPinIconOutline className="h-6 w-6 group-hover:hidden" />
                <PushPinIconSolid className="h-6 w-6 hidden group-hover:block" />
              </>
            )}
          </button>
        )}
        {showHighlight && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onHighlight?.();
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                e.stopPropagation();
                onHighlight?.();
              }
            }}
            tabIndex={buttonTabIndex}
            className={classNames.join(' ')}
            aria-label={isHighlighted ? 'Remove highlight' : 'Highlight objective'}
            aria-pressed={isHighlighted}
          >
            {isHighlighted ? (
              <ViewfinderCircleIconSolid className="h-6 w-6" />
            ) : (
              <>
                <ViewfinderCircleIconOutline className="h-6 w-6 group-hover:hidden" />
                <ViewfinderCircleIconSolid className="h-6 w-6 hidden group-hover:block" />
              </>
            )}
          </button>
        )}
        {showRemove && (
          <>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setIsRemoveAlertOpen(true);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  e.stopPropagation();
                  setIsRemoveAlertOpen(true);
                }
              }}
              tabIndex={buttonTabIndex}
              className={classNames.join(' ')}
              aria-label="Remove item from map"
            >
              <MinusIcon className="h-6 w-6 group-hover:hidden" />
              <MinusIconSolid className="h-6 w-6 hidden group-hover:block" />
            </button>

            <Alert
              open={isRemoveAlertOpen}
              onClose={() => setIsRemoveAlertOpen(false)}
              className="relative z-50"
            >
              <AlertTitle>Remove from map?</AlertTitle>
              <AlertDescription>
                The objective will still exist but won&apos;t appear on this map.
              </AlertDescription>
              <AlertActions>
                <Button plain onClick={() => setIsRemoveAlertOpen(false)}>
                  Cancel
                </Button>
                <Button
                  onClick={() => {
                    onRemove?.();
                    setIsRemoveAlertOpen(false);
                  }}
                >
                  Remove
                </Button>
              </AlertActions>
            </Alert>
          </>
        )}
      </div>
    </div>
  );
}
